import { create } from 'zustand';
import { mutative } from 'zustand-mutative';

import type { GetUserUis, RbacUser, UserUiItem } from './types';

const getRbacStorageKey = (userId: string) => `ui:permissions:${userId}`;

export function computeAppPermissions(uiPermissions: string[]): string[] {
  const set = new Set<string>();
  for (const key of uiPermissions ?? []) {
    const segment = key.trim().split(':')[0]?.trim();
    if (segment) set.add(segment);
  }
  return Array.from(set).sort();
}

const CACHE_SHAPE = {
  uiPermissions: [] as string[],
  routePermissions: [] as string[],
  appPermissions: [] as string[],
};

export function getCachedPermissions(
  userId: string,
): typeof CACHE_SHAPE | null {
  try {
    const raw = sessionStorage.getItem(getRbacStorageKey(userId));
    if (!raw) return null;
    const data = JSON.parse(raw) as typeof CACHE_SHAPE;
    if (
      !Array.isArray(data?.uiPermissions) ||
      !Array.isArray(data?.routePermissions) ||
      !Array.isArray(data?.appPermissions)
    )
      return null;
    return {
      uiPermissions: data.uiPermissions,
      routePermissions: data.routePermissions,
      appPermissions: data.appPermissions,
    };
  } catch {
    return null;
  }
}

export function setCachedPermissions(
  userId: string,
  data: {
    uiPermissions: string[];
    routePermissions: string[];
    appPermissions: string[];
  },
) {
  try {
    sessionStorage.setItem(getRbacStorageKey(userId), JSON.stringify(data));
  } catch {
    // ignore
  }
}

export const useRbacStore = create<{
  appPermissions: string[];
  uiPermissions: string[];
  routePermissions: string[];
  permissionsLoading: boolean;
  whitelistMode: boolean;
}>()(
  mutative((_set) => {
    return {
      appPermissions: [],
      uiPermissions: [],
      routePermissions: [],
      permissionsLoading: true,
      whitelistMode: false,
    };
  }),
);

export async function initPermissions(
  user: RbacUser,
  getUserUisApi: GetUserUis,
  // appType?: ('console' | 'arc')[],
) {
  const allKeys = new Set<string>();
  const allRoutes = new Set<string>();
  let raw: UserUiItem[] = [];
  try {
    raw = await getUserUisApi(user.id);
  } catch (e) {
    raw = [];
  }
  raw.forEach((item) => {
    allKeys.add(item.key);
    allRoutes.add(item.route);
  });
  const newUi = Array.from(allKeys).sort();
  const newRoutes = Array.from(allRoutes).sort();
  const newApp = computeAppPermissions(newUi);
  const {
    uiPermissions: curUi,
    routePermissions: curRoutes,
    appPermissions: curApp,
  } = useRbacStore.getState();
  const same =
    curUi.length === newUi.length &&
    curRoutes.length === newRoutes.length &&
    JSON.stringify(curUi.slice().sort()) === JSON.stringify(newUi) &&
    JSON.stringify(curRoutes.slice().sort()) === JSON.stringify(newRoutes) &&
    JSON.stringify(curApp.slice().sort()) === JSON.stringify(newApp);
  if (same) return;
  useRbacStore.setState({
    uiPermissions: newUi,
    routePermissions: newRoutes,
    appPermissions: newApp,
  });
  setCachedPermissions(user.id, {
    uiPermissions: newUi,
    routePermissions: newRoutes,
    appPermissions: newApp,
  });
}
